import { startOfDay, subDays } from "date-fns";
import { ArrowDown, ArrowUp, ArrowUpDown, Building2 } from "lucide-react";
import {
  FilterLiveForm,
  useGetList,
  useListContext,
  useListSortContext,
  useTranslate,
  ListBase,
} from "ra-core";
import { useMemo } from "react";
import { SearchInput } from "@/components/admin/search-input";
import { ListPagination } from "@/components/admin/list-pagination";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Sale } from "../types";
import { AccountsCardList } from "./AccountsCardList";
import { AccountsSavedViews } from "./AccountsSavedViews";
import { AccountsTableRow } from "./AccountsTableRow";
import { DEFAULT_ACCOUNTS_SORT, applyDefaultTieBreak } from "./accountsSort";
import { useDashboardScope } from "./useDashboardScope";

const ACCOUNTS_PER_PAGE = 10;
const QUIET_DAYS = 30;
const SALES_LIMIT = 100;
/** Sentinel for "no value" — a shadcn <SelectItem> cannot hold an empty value. */
const ANY_VALUE = "any";

/**
 * How the accounts are laid out. `cards` is the narrow, one-column reading of
 * the same list, for the mobile home screen: no sortable header row and no
 * filter bar, since a phone home screen is a triage list rather than a query
 * surface.
 */
export type AccountsTableVariant = "table" | "cards";

/**
 * The companies list, one row per account, with its open task counts, next
 * action, open pipeline and last touch. Rows expand in place to show the
 * account's open tasks.
 */
export const AccountsTable = ({
  variant = "table",
}: {
  variant?: AccountsTableVariant;
}) => {
  const translate = useTranslate();
  const { isTeam, salesId, isPending } = useDashboardScope();
  const isCards = variant === "cards";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <div className="mr-3 flex">
          <Building2 className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground flex-1">
          {translate("crm.dashboard.accounts.title")}
        </h2>
      </div>
      {/*
        The scope decides which account manager's companies are listed, so
        rendering before it resolves would fire a second, different query the
        moment it lands.
      */}
      {isPending ? (
        <AccountsTableSkeleton />
      ) : (
        <ListBase<Company>
          resource="companies"
          perPage={ACCOUNTS_PER_PAGE}
          sort={DEFAULT_ACCOUNTS_SORT}
          /*
            The dashboard is the "what am I working on" surface, so it lists
            only accounts that are live work: an open task, or activity in the
            last ninety days. The Companies page is still the full list.
          */
          filter={{
            ...(isTeam ? {} : { sales_id: salesId }),
            is_active: true,
          }}
          storeKey="dashboard.accounts"
          /*
            The dashboard scope owns the query string (?owner=&period=). Syncing
            this widget's own filters there as well would make the two fight
            over the same location.
          */
          disableSyncWithLocation
        >
          <div className="flex flex-col gap-3">
            {isCards ? null : (
              <>
                <AccountsFilters isTeam={isTeam} />
                <AccountsSavedViews />
              </>
            )}
            {isCards ? (
              <AccountsCardList />
            ) : (
              <Card className="p-0 overflow-hidden">
                <AccountsTableView />
              </Card>
            )}
            <ListPagination rowsPerPageOptions={[10, 25, 50]} />
          </div>
        </ListBase>
      )}
    </div>
  );
};

const AccountsTableView = () => {
  const translate = useTranslate();
  const { data, sort, isPending } = useListContext<Company>();

  if (isPending) return <AccountsTableSkeleton />;

  if (!data?.length) {
    return (
      <p className="text-sm text-muted-foreground p-4">
        {translate("crm.dashboard.accounts.empty")}
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <SortableHead
            field="name"
            label="crm.dashboard.accounts.col_company"
          />
          <SortableHead
            field="sales_id"
            label="crm.dashboard.accounts.col_owner"
          />
          {/*
            The Open column sorts on the overdue count rather than on the open
            one: it is the number the column exists for, and it keeps the
            default order reachable after the user has sorted on something else.
          */}
          <SortableHead
            field="nb_overdue_tasks"
            label="crm.dashboard.accounts.col_open"
            firstOrder="DESC"
          />
          {/*
            The next open task of the account, which is the next touch of its
            cadence: the same column the card layout calls the next action.
          */}
          <SortableHead
            field="next_task_due"
            label="crm.dashboard.accounts.col_next_touch"
          />
          <SortableHead
            field="open_deals_amount"
            label="crm.dashboard.accounts.col_pipeline"
            firstOrder="DESC"
            className="text-right"
          />
          <SortableHead
            field="last_activity_at"
            label="crm.dashboard.accounts.col_last_touch"
          />
        </TableRow>
      </TableHeader>
      <TableBody>
        {applyDefaultTieBreak(data, sort).map((company) => (
          <AccountsTableRow key={company.id} company={company} />
        ))}
      </TableBody>
    </Table>
  );
};

const SortableHead = ({
  field,
  label,
  firstOrder = "ASC",
  className,
}: {
  field: string;
  label: string;
  firstOrder?: "ASC" | "DESC";
  className?: string;
}) => {
  const translate = useTranslate();
  const { sort, setSort } = useListSortContext();
  const isActive = sort.field === field;
  const nextOrder = isActive
    ? sort.order === "ASC"
      ? "DESC"
      : "ASC"
    : firstOrder;

  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => setSort({ field, order: nextOrder })}
        className="inline-flex items-center gap-1 cursor-pointer rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {translate(label)}
        {!isActive ? (
          <ArrowUpDown className="size-3 opacity-40" />
        ) : sort.order === "ASC" ? (
          <ArrowUp className="size-3" />
        ) : (
          <ArrowDown className="size-3" />
        )}
      </button>
    </TableHead>
  );
};

const AccountsFilters = ({ isTeam }: { isTeam: boolean }) => {
  const translate = useTranslate();
  const { companySectors } = useConfigurationContext();
  const { filterValues, setFilters } = useListContext<Company>();
  const { data: sales } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: SALES_LIMIT },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: isTeam },
  );

  // Frozen for the lifetime of the bar: ToggleFilterButton decides whether it
  // is active by comparing its value with the current filters, so a threshold
  // recomputed on every render would never match the one already applied — the
  // button would look off while filtering, and clicking it again would add a
  // second, slightly different filter instead of removing the first.
  const quietSince = useMemo(
    () => startOfDay(subDays(new Date(), QUIET_DAYS)).toISOString(),
    [],
  );

  // Clearing drops the key rather than setting it to undefined: an undefined
  // value still reaches the data provider, where it reads as "match null".
  const setFilter = (source: string, value: string) => {
    const others = Object.fromEntries(
      Object.entries(filterValues).filter(([key]) => key !== source),
    );
    setFilters(value === ANY_VALUE ? others : { ...others, [source]: value });
  };

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      <div className="w-full sm:w-56">
        <FilterLiveForm>
          <SearchInput source="q" />
        </FilterLiveForm>
      </div>

      <Select
        value={filterValues.sector ?? ANY_VALUE}
        onValueChange={(value) => setFilter("sector", value)}
      >
        <SelectTrigger
          size="sm"
          className="w-40"
          aria-label={translate("resources.companies.fields.sector")}
        >
          <SelectValue
            placeholder={translate("resources.companies.fields.sector")}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY_VALUE}>
            {translate("resources.companies.fields.sector")}
          </SelectItem>
          {companySectors.map((sector) => (
            <SelectItem key={sector.value} value={sector.value}>
              {sector.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/*
        The scope already pins the list to one account manager unless the whole
        team is shown, so this filter would have nothing left to narrow.
      */}
      {isTeam && (
        <Select
          value={
            filterValues.sales_id != null
              ? String(filterValues.sales_id)
              : ANY_VALUE
          }
          onValueChange={(value) => setFilter("sales_id", value)}
        >
          <SelectTrigger
            size="sm"
            className="w-44"
            aria-label={translate("crm.dashboard.scope.owner")}
          >
            <SelectValue placeholder={translate("crm.dashboard.scope.owner")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_VALUE}>
              {translate("crm.dashboard.scope.owner")}
            </SelectItem>
            {sales?.map((sale) => (
              <SelectItem key={sale.id} value={String(sale.id)}>
                {`${sale.first_name} ${sale.last_name}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <ToggleFilterButton
        className="w-auto"
        label="crm.dashboard.accounts.filter_has_overdue"
        value={{ "nb_overdue_tasks@gt": 0 }}
      />
      {/*
        An account never touched at all has a null `last_activity_at`, which no
        comparison operator matches. It is left out here, the same way the
        contact list's "not contacted since" filters leave out contacts that
        were never seen.
      */}
      <ToggleFilterButton
        className="w-auto"
        label="crm.dashboard.accounts.filter_quiet"
        value={{ "last_activity_at@lt": quietSince }}
      />
      <ToggleFilterButton
        className="w-auto"
        label="crm.dashboard.accounts.filter_has_deals"
        value={{ "open_deals_amount@gt": 0 }}
      />
    </div>
  );
};

const AccountsTableSkeleton = () => (
  <div className="flex flex-col gap-2 p-4">
    {Array.from({ length: 5 }, (_, index) => (
      <Skeleton key={index} className="h-10 w-full" />
    ))}
  </div>
);
